import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Modal } from '../components/Modal.jsx';

export default function Users() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['users'], queryFn: () => api('/api/users') });
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const save = useMutation({
    mutationFn: payload => payload.id
      ? api(`/api/users/${payload.id}`, { method: 'PATCH', body: payload.data })
      : api('/api/users', { method: 'POST', body: payload.data }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setEditing(null); setCreating(false); },
  });

  const del = useMutation({
    mutationFn: id => api(`/api/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <>
      <div className="page-header">
        <h1>{t('users.title')}</h1>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>{t('users.add')}</button>
      </div>
      {isLoading ? <div className="card">{t('common.loading')}</div> : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('common.email')}</th>
              <th>{t('common.role')}</th>
              <th>{t('common.language')}</th>
              <th>2FA</th>
              <th>{t('common.active')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {data?.users?.map(u => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td><span className={`badge badge-${u.role.toLowerCase()}`}>{t(`roles.${u.role}`)}</span></td>
                <td>{u.language.toUpperCase()}</td>
                <td>{u.totpEnabled ? t('common.yes') : t('common.no')}</td>
                <td>{u.active ? t('common.yes') : <span className="badge badge-inactive">{t('common.no')}</span>}</td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(u)}>{t('common.edit')}</button>{' '}
                  {u.role !== 'OWNER' && u.active && (
                    <button className="btn btn-danger btn-sm" onClick={() => window.confirm(t('common.confirmDelete')) && del.mutate(u.id)}>
                      {t('common.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(creating || editing) && (
        <UserModal
          user={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSave={data => save.mutate({ id: editing?.id, data })}
          saving={save.isPending}
          error={save.error?.data}
        />
      )}
    </>
  );
}

function UserModal({ user, onClose, onSave, saving, error }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role === 'OWNER' ? 'ADMIN' : (user?.role || 'VIEWER'),
    language: user?.language || 'en',
    password: '',
    active: user?.active ?? true,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = e => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    if (user) delete payload.email; // email immutable
    onSave(payload);
  };

  return (
    <Modal title={user ? t('common.edit') : t('users.add')} onClose={onClose}>
      <form onSubmit={submit}>
        {error && <div className="error-banner">{error.error}</div>}
        <div className="field">
          <label>{t('common.name')}</label>
          <input required value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        {!user && (
          <div className="field">
            <label>{t('common.email')}</label>
            <input type="email" required value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
        )}
        <div className="row">
          <div className="field">
            <label>{t('common.role')}</label>
            <select value={form.role} onChange={e => set('role', e.target.value)}>
              <option value="ADMIN">{t('roles.ADMIN')}</option>
              <option value="ACCOUNTANT">{t('roles.ACCOUNTANT')}</option>
              <option value="VIEWER">{t('roles.VIEWER')}</option>
            </select>
          </div>
          <div className="field">
            <label>{t('common.language')}</label>
            <select value={form.language} onChange={e => set('language', e.target.value)}>
              <option value="en">EN</option>
              <option value="sq">SQ</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label>{user ? 'New password (leave blank to keep)' : t('auth.password')}</label>
          <input type="password" minLength={10} required={!user} value={form.password} onChange={e => set('password', e.target.value)} />
        </div>
        {user && (
          <div className="field">
            <label><input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} /> {t('common.active')}</label>
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}
